import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { useApi } from "@/lib/api";

/**
 * Compact creators list. One row per creator, the whole row opens the detail
 * page. Secondary action: Live page (small link). Everything else moves into
 * a row menu (Latest draft, Archive). No full-width pill buttons.
 *
 * Default view hides is_internal and archived creators. Toggle at the top.
 */

function StatusChip({ value }) {
    const map = {
        named: { label: "Named", bg: "bg-signal/15", fg: "text-signal" },
        not_yet_named: {
            label: "Not yet named",
            bg: "bg-black/5",
            fg: "text-black/70",
        },
        no_run_yet: {
            label: "No run yet",
            bg: "bg-black/5",
            fg: "text-black/50",
        },
    };
    const m = map[value] || map.no_run_yet;
    return (
        <span
            data-testid={`creators-row-status-${value}`}
            className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-mono uppercase tracking-wider ${m.bg} ${m.fg}`}
        >
            {m.label}
        </span>
    );
}

function StageChip({ item }) {
    let stage = "New";
    let fg = "text-black/70";
    if (item.ownership_status === "pending_staff" && !item.approved_at) {
        stage = "Pending ownership";
        fg = "text-black/60";
    } else if (item.approved_at) {
        stage = "Live";
        fg = "text-electric";
    } else if (item.page_status === "held") {
        stage = "Held";
        fg = "text-black/60";
    } else if (item.latest_draft_id) {
        stage = "Draft";
        fg = "text-black/60";
    }
    return (
        <span
            data-testid={`creators-row-stage-${item.id}`}
            className={`text-[11px] font-mono uppercase tracking-wider ${fg}`}
        >
            {stage}
        </span>
    );
}

function fmtDate(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        return d.toLocaleDateString(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    } catch {
        return "";
    }
}

function RowMenu({ item, onArchived }) {
    const { request } = useApi();
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);

    const archive = useCallback(
        async (e) => {
            e.stopPropagation();
            if (!window.confirm(`Archive /@${item.samoony_handle}?`)) return;
            setBusy(true);
            try {
                await request(`/admin/creators/${item.id}/archive`, {
                    method: "POST",
                });
                toast.success("Archived.");
                onArchived?.(item.id);
            } catch (err) {
                toast.error(err?.message || "Could not archive.");
            } finally {
                setBusy(false);
                setOpen(false);
            }
        },
        [item, request, onArchived],
    );

    return (
        <div className="relative" onClick={(e) => e.stopPropagation()}>
            <button
                data-testid={`creators-row-menu-${item.id}`}
                onClick={(e) => {
                    e.stopPropagation();
                    setOpen((v) => !v);
                }}
                className="h-8 w-8 inline-flex items-center justify-center rounded-full text-black/60 hover:bg-black/5"
                aria-label="Row menu"
            >
                <span aria-hidden="true" className="text-lg leading-none">
                    …
                </span>
            </button>
            {open && (
                <div
                    data-testid={`creators-row-menu-popover-${item.id}`}
                    className="absolute right-0 mt-1 w-56 bg-white border border-black/10 rounded-xl shadow-lg py-1 z-10"
                >
                    {item.latest_draft_path ? (
                        <a
                            data-testid={`creators-row-latest-draft-${item.id}`}
                            href={item.latest_draft_path}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block px-3 py-2 text-sm hover:bg-black/5"
                        >
                            Open latest draft
                        </a>
                    ) : (
                        <span className="block px-3 py-2 text-sm text-black/40">
                            No draft yet
                        </span>
                    )}
                    {item.archivable && (
                        <button
                            data-testid={`creators-row-archive-${item.id}`}
                            onClick={archive}
                            disabled={busy}
                            className="block w-full text-left px-3 py-2 text-sm hover:bg-black/5 disabled:opacity-40"
                        >
                            {busy ? "Archiving..." : "Archive"}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

export default function AdminCreatorsList() {
    const { request } = useApi();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState("");
    const [showInternal, setShowInternal] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (query.trim()) params.set("q", query.trim());
            if (showInternal) {
                params.set("include_internal", "1");
                params.set("include_archived", "1");
            }
            const qs = params.toString();
            const res = await request(
                `/admin/creators${qs ? `?${qs}` : ""}`,
            );
            setItems(res?.creators || []);
        } catch (err) {
            toast.error(err?.message || "Could not load creators.");
        } finally {
            setLoading(false);
        }
    }, [request, query, showInternal]);

    useEffect(() => {
        load();
    }, [load]);

    const summary = useMemo(() => {
        const total = items.length;
        const named = items.filter((i) => i.name_status === "named").length;
        return `${total} ${total === 1 ? "creator" : "creators"} · ${named} named`;
    }, [items]);

    return (
        <section>
            <header className="flex flex-wrap items-end justify-between gap-3 mb-5">
                <div>
                    <h1 className="font-display text-3xl font-bold tracking-tight">
                        Creators
                    </h1>
                    <p
                        data-testid="creators-list-summary"
                        className="text-sm text-black/60 mt-1"
                    >
                        {summary}
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <input
                        data-testid="creators-list-search"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search handle, name or email"
                        className="samoony-input !h-9 w-64 max-w-[70vw]"
                    />
                    <label className="inline-flex items-center gap-2 text-xs text-black/60 cursor-pointer select-none">
                        <input
                            data-testid="creators-list-toggle-internal"
                            type="checkbox"
                            checked={showInternal}
                            onChange={(e) =>
                                setShowInternal(e.target.checked)
                            }
                        />
                        Show internal and archived
                    </label>
                </div>
            </header>

            <div
                data-testid="creators-list"
                className="bg-white border border-black/10 rounded-2xl divide-y divide-black/10"
            >
                {loading && (
                    <div className="p-4 text-sm text-black/50">
                        Loading creators...
                    </div>
                )}
                {!loading && items.length === 0 && (
                    <div
                        data-testid="creators-list-empty"
                        className="p-6 text-sm text-black/60"
                    >
                        No creators match.
                    </div>
                )}
                {!loading &&
                    items.map((it) => (
                        <Row
                            key={it.id}
                            item={it}
                            onArchived={(id) =>
                                setItems((prev) =>
                                    prev.filter((p) => p.id !== id),
                                )
                            }
                        />
                    ))}
            </div>
        </section>
    );
}

function Row({ item, onArchived }) {
    const livePath = item.live_page_path || null;
    const detailPath = `/admin/creators/${item.id}`;
    return (
        <Link
            to={detailPath}
            data-testid={`creators-row-${item.id}`}
            className="flex items-center gap-3 px-4 py-3 hover:bg-black/[0.03] no-underline text-ink"
        >
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                    <span
                        data-testid={`creators-row-handle-${item.id}`}
                        className="font-mono text-sm text-black/80"
                    >
                        /@{item.samoony_handle || "no-handle"}
                    </span>
                    <StatusChip value={item.name_status} />
                    {item.open_flags > 0 && (
                        <span
                            data-testid={`admin-creator-not-me-${item.id}`}
                            className="font-mono text-[10px] uppercase tracking-widest rounded-full px-2 py-0.5 bg-signal-tint text-signal-text"
                        >
                            Not me
                        </span>
                    )}
                    <StageChip item={item} />
                    {item.is_internal && (
                        <span className="text-[11px] font-mono uppercase tracking-wider text-electric">
                            Internal
                        </span>
                    )}
                    {item.archived_at && (
                        <span className="text-[11px] font-mono uppercase tracking-wider text-black/40">
                            Archived
                        </span>
                    )}
                </div>
                <div className="text-sm text-black/70 truncate mt-0.5">
                    {item.display_name || item.first_name || ""}
                    {item.email ? (
                        <>
                            <span className="text-black/30"> · </span>
                            <span className="font-mono text-xs text-black/50">
                                {item.email}
                            </span>
                        </>
                    ) : null}
                </div>
                <div className="text-xs text-black/40 mt-0.5">
                    Updated {fmtDate(item.updated_at || item.created_at)}
                </div>
            </div>
            {livePath && (
                <a
                    data-testid={`creators-row-live-${item.id}`}
                    href={livePath}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="text-xs font-mono text-black/50 hover:text-ink underline underline-offset-4"
                >
                    Live page ↗
                </a>
            )}
            <RowMenu item={item} onArchived={onArchived} />
        </Link>
    );
}
